// Lazy document-edge detection using OpenCV.js.
// Loaded on demand so it never affects initial page load. Finds the largest
// 4-corner contour (the card/document), then perspective-warps it to a tight,
// upright rectangle so the card fills the frame. Falls back gracefully when the
// library cannot load or no confident edge is found.

const OPENCV_URL = 'https://docs.opencv.org/4.9.0/opencv.js';
let _cvPromise = null;

export function loadOpenCv() {
  if (_cvPromise) return _cvPromise;
  _cvPromise = new Promise((resolve, reject) => {
    let settled = false;
    let timeout = null;
    const removeScript = () => { const s = document.getElementById('opencv-js'); if (s) s.remove(); };
    const done = (cv) => { if (settled) return; settled = true; if (timeout) clearTimeout(timeout); resolve(cv); };
    const fail = (e) => { if (settled) return; settled = true; if (timeout) clearTimeout(timeout); _cvPromise = null; removeScript(); reject(e); };
    // The Emscripten module is itself a thenable; resolving a Promise with it
    // triggers an infinite thenable loop. Await its readiness, then strip .then
    // before handing a plain module reference back.
    const whenReady = (cv) => {
      if (!cv) { fail(new Error('cv missing')); return; }
      if (typeof cv.then === 'function') {
        cv.then((m) => { try { delete m.then; } catch (_) {} done(m); }, fail);
      } else if (cv.Mat) {
        done(cv);
      } else {
        cv.onRuntimeInitialized = () => { try { delete cv.then; } catch (_) {} done(cv); };
      }
    };
    timeout = setTimeout(() => fail(new Error('OpenCV load timeout')), 25000);
    if (window.cv) { whenReady(window.cv); return; }
    const s = document.createElement('script');
    s.id = 'opencv-js';
    s.async = true;
    s.src = OPENCV_URL;
    s.onload = () => { whenReady(window.cv); };
    s.onerror = () => { fail(new Error('Failed to load OpenCV.js')); };
    document.body.appendChild(s);
  });
  return _cvPromise;
}

const loadImage = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => res(img);
  img.onerror = rej;
  img.src = src;
});

function orderCorners(pts) {
  // pts: [{x,y}*4] -> [tl, tr, br, bl]
  const sorted = [...pts].sort((a, b) => a.y - b.y);
  const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
  const bot = sorted.slice(2).sort((a, b) => a.x - b.x);
  return [top[0], top[1], bot[1], bot[0]];
}
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Returns a data URL of the deskewed card, or null when no confident quad found.
export async function detectAndWarp(src) {
  const cv = await loadOpenCv();
  const img = await loadImage(src);

  // Work on a downscaled copy for speed; warp the full-resolution image.
  const MAXW = 1600;
  const scale = Math.min(1, MAXW / img.width);
  const sw = Math.round(img.width * scale), sh = Math.round(img.height * scale);
  const small = document.createElement('canvas');
  small.width = sw; small.height = sh;
  small.getContext('2d').drawImage(img, 0, 0, sw, sh);

  const srcMat = cv.imread(small);
  const gray = new cv.Mat();
  const blur = new cv.Mat();
  const edges = new cv.Mat();
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();
  let best = null;
  try {
    cv.cvtColor(srcMat, gray, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0);
    cv.Canny(blur, edges, 60, 180);
    const k = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
    cv.dilate(edges, edges, k);
    k.delete();
    cv.findContours(edges, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

    const imgArea = sw * sh;
    let bestArea = 0;
    for (let i = 0; i < contours.size(); i++) {
      const cnt = contours.get(i);
      const area = cv.contourArea(cnt);
      if (area < imgArea * 0.18) { cnt.delete(); continue; }
      const peri = cv.arcLength(cnt, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(cnt, approx, 0.02 * peri, true);
      if (approx.rows === 4 && cv.isContourConvex(approx) && area > bestArea) {
        bestArea = area;
        const pts = [];
        for (let j = 0; j < 4; j++) pts.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] });
        best = pts;
      }
      approx.delete();
      cnt.delete();
    }
  } finally {
    srcMat.delete(); gray.delete(); blur.delete(); edges.delete();
    contours.delete(); hierarchy.delete();
  }

  if (!best) return null;

  // Scale corners back to full-resolution coordinates and warp the original.
  const corners = orderCorners(best).map((p) => ({ x: p.x / scale, y: p.y / scale }));
  const [tl, tr, br, bl] = corners;
  const outW = Math.round(Math.max(dist(tl, tr), dist(bl, br)));
  const outH = Math.round(Math.max(dist(tl, bl), dist(tr, br)));
  if (outW < 80 || outH < 80) return null;

  const full = document.createElement('canvas');
  full.width = img.width; full.height = img.height;
  full.getContext('2d').drawImage(img, 0, 0);
  const fullMat = cv.imread(full);
  const dstMat = new cv.Mat();
  const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]);
  const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, outW, 0, outW, outH, 0, outH]);
  const M = cv.getPerspectiveTransform(srcTri, dstTri);
  const outCanvas = document.createElement('canvas');
  outCanvas.width = outW; outCanvas.height = outH;
  try {
    cv.warpPerspective(fullMat, dstMat, M, new cv.Size(outW, outH), cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar(255, 255, 255, 255));
    cv.imshow(outCanvas, dstMat);
  } finally {
    fullMat.delete(); dstMat.delete(); srcTri.delete(); dstTri.delete(); M.delete();
  }

  return { dataUrl: outCanvas.toDataURL('image/jpeg', 0.92), width: outW, height: outH };
}
